import { useState } from 'react';
import axios from 'axios';
import { useAppDispatch } from '@redux/hooks';
import * as yup from 'yup';
import { useFormik } from 'formik';

import { MenuItem, Stack, TextField } from '@mui/material';
import { FileInput, FormDialog } from 'components/common';

import { setSnackbar } from '@redux/snackbar';
import { REPORT_ISSUE_TYPE, SnackbarType } from '@constants';
import { logEvent } from '@services';

export const ReportIssue: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik<{
    issueType: string;
    issueDescription: string;
    attachments: File[];
  }>({
    initialValues: {
      issueType: 'General feedback',
      issueDescription: '',
      attachments: [],
    },
    validationSchema: yup.object({
      issueType: yup.string().required('Issue type is required'),
      issueDescription: yup
        .string()
        .max(5000)
        .required('Issue description is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);

      const form = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value) {
          if (Array.isArray(value)) {
            for (const file of value) {
              form.append(key, file);
            }
          } else {
            form.append(key, value);
          }
        }
      });

      try {
        await axios.post(`/report-issue`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        dispatch(
          setSnackbar({
            type: SnackbarType.Info,
            message: 'Issue has been reported.',
          }),
        );
        resetForm();
        onClose();
        logEvent('report_issue_saved');
      } catch (e: any) {
        dispatch(
          setSnackbar({
            type: SnackbarType.Error,
            message: `Issue hasn't been reported due to ${e.message}.`,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const issueDescriptionError =
    formik.touched.issueDescription && formik.errors.issueDescription;

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      label="Report an issue"
      isLoading={isLoading}
      submitDisabled={!formik.dirty || !formik.isValid}
      onSubmit={formik.handleSubmit}
    >
      <Stack gap={2}>
        <TextField
          select
          fullWidth
          required
          name="issueType"
          label="Issue type"
          InputLabelProps={{ shrink: true }}
          value={formik.values.issueType}
          onChange={formik.handleChange}
          error={formik.touched.issueType && !!formik.errors.issueType}
          helperText={formik.touched.issueType && formik.errors.issueType}
        >
          {Object.values(REPORT_ISSUE_TYPE).map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          name="issueDescription"
          label="Description"
          placeholder="Please describe any questions or ideas you have."
          InputLabelProps={{ shrink: true }}
          value={formik.values.issueDescription}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          required
          multiline
          minRows={5}
          error={!!issueDescriptionError}
          helperText={
            issueDescriptionError ||
            'Someone from AgileEngine may email you for further clarification.'
          }
        />
        <FileInput
          hint="Max 10 files, 5 MB each"
          name="attachments"
          multiple
          onChange={({ files }) => formik.setFieldValue('attachments', files)}
        />
      </Stack>
    </FormDialog>
  );
};
